<script setup>
import { ref } from "vue";
import FormGroup from "@/components/form/FormGroup.vue";
import TermsOfUseModal from "@/components/legal/TermsOfUseModal.vue";
import PrivacyPolicyModal from "@/components/legal/PrivacyPolicyModal.vue";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { ClientError, getMessageFromError } from "@/libs/errors";
import { useAuthStore } from "@/stores/auth";
import { useRouter } from "vue-router";

const showPassword = ref(false);
const showPasswordConfirmation = ref(false);
const showTermsModal = ref(false);
const showPrivacyPolicyModal = ref(false);
const errorMessage = ref("");
const loading = ref(false);
const email = ref("");
const password = ref("");
const passwordConfirmation = ref("");
const authStore = useAuthStore();
const router = useRouter();

const { mutate: createUser } = useMutation(
  gql`
    mutation CreateUser($input: NewUserInput!) {
      createUser(input: $input) {
        id
      }
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

// const { mutate : updateUserProfile } = useMutation(gql`
// mutation UpdateUserProfile($input: UpdateUserProfileInput!) {
//   updateUserProfile(input: $input) {
//     id
//   }
// }
// `,{
//   fetchPolicy: 'no-cache',
// })

const submit = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    if (password.value !== passwordConfirmation.value) {
      throw new ClientError("As senhas não são iguais. Tente novamente.");
    }
    if (!password.value || !passwordConfirmation.value || !email.value) {
      throw new ClientError("Preencha todos os campos. Tente novamente.");
    }
    await createUser({
      input: {
        email: email.value,
        password: password.value,
      },
    });
    await authStore.login(email.value, password.value);
    router.push("/auth/mobile-register/step-2");
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  }
  loading.value = false;
};
</script>

<template>
  <div class="login">
    <div class="container">
      <div class="content-center">
        <div class="row">
          <div class="col col-12 d-block">
            <h2 class="mb-4">
              <a href="https://www.quantofaz.com.br">
                <img src="@/assets/images/logo.svg" alt="logo" width="300" />
              </a>
            </h2>
          </div>
          <div class="col col-12">
            <div class="card-plain">
              <header>
                <div class="progress mb-4">
                  <div
                    class="progress-bar bg-pattern"
                    role="progressbar"
                    style="width: 33.339%"
                    aria-valuenow="25"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <h1>Crie sua conta</h1>
              </header>
              <form @submit.prevent="submit">
                <div v-if="errorMessage" class="alert alert-danger">
                  {{ errorMessage }}
                </div>
                <FormGroup label="E-mail" labelFor="email" class="mb-3">
                  <input
                    id="email"
                    type="email"
                    class="form-control"
                    placeholder="Digite seu melhor e-mail"
                    v-model="email"
                  />
                </FormGroup>
                <FormGroup
                  label="Digite sua Senha"
                  labelFor="password"
                  class="mb-3"
                >
                  <input
                    id="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="password"
                  />
                  <template #append-slot>
                    <span
                      class="input-group-text"
                      :class="{ active: showPassword }"
                      @click="showPassword = !showPassword"
                    >
                      <svg
                        v-if="showPassword"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye-slash"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
                        />
                        <path
                          d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
                        />
                        <path
                          d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
                        />
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                        />
                        <path
                          d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                        />
                      </svg>
                    </span>
                  </template>
                </FormGroup>
                <FormGroup
                  label="Repita sua Senha"
                  labelFor="password-confirmation"
                  class="mb-4"
                >
                  <input
                    id="password-confirmation"
                    :type="showPasswordConfirmation ? 'text' : 'password'"
                    class="form-control"
                    v-model="passwordConfirmation"
                  />
                  <template #append-slot>
                    <span
                      class="input-group-text"
                      :class="{ active: showPasswordConfirmation }"
                      @click="
                        showPasswordConfirmation = !showPasswordConfirmation
                      "
                    >
                      <svg
                        v-if="showPasswordConfirmation"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye-slash"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
                        />
                        <path
                          d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
                        />
                        <path
                          d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
                        />
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                        />
                        <path
                          d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                        />
                      </svg>
                    </span>
                  </template>
                </FormGroup>
                <FormGroup labelFor="terms-agreement">
                  <div class="form-check policiesAgreement">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      value=""
                      id="terms-agreement"
                      required
                    />
                    <label class="form-check-label" for="terms-agreement">
                      Li e aceito os
                      <a
                        href="#"
                        @click.prevent="showTermsModal = true"
                        >Termos de Uso</a
                      >
                      e as
                      <a
                        href="#"
                        @click.prevent="showPrivacyPolicyModal = true"
                        >Políticas de Privacidade</a
                      >
                    </label>
                  </div>
                </FormGroup>

                <div class="buttons d-grid gap-2">
                  <button
                    v-if="loading"
                    type="submit"
                    class="btn btn-primary btn-block"
                    disabled
                  >
                    CADASTRANDO...
                  </button>
                  <button
                    v-else
                    type="submit"
                    class="btn btn-primary btn-block"
                  >
                    CONTINUAR
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <TermsOfUseModal v-if="showTermsModal" @close="showTermsModal = false" />
  <PrivacyPolicyModal v-if="showPrivacyPolicyModal" @close="showPrivacyPolicyModal = false" />
</template>

<style scoped>
.login {
  height: 100vh;
  max-height: 1050px;
  padding: 0;
  position: relative;
  background-color: var(--black-eigengrau);
}

.login:before {
  content: "";
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 184px;
  left: 0;
}

.content-center {
  position: relative;
  margin: 0 auto;
  z-index: 99;
  padding: 56px 0 0;
}

h2 {
  font-size: 1.5625rem;
  margin: 0;
  padding: 1.5625rem 0 0.55rem;
  text-align: center;
}

h2 a {
  color: #030303;
  text-decoration: none;
}
.policiesAgreement {
  font-size: 12px;
}

.card-plain {
  margin: 0 auto 50px;
  border-radius: 0.5rem;
  max-width: 330px;
  padding: 20px;
  color: var(--white-ice);
}

.card-plain h1 {
  font-size: 1.5rem;
  line-height: 1.4em;
  margin-bottom: 20px;
  text-align: center;
  color: var(--primary-color);
}

.card-plain .form-control {
  background: var(--black-eigengrau);
  border-color: var(--primary-color);
  color: var(--white-ice);
}

.card-plain .form-control:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
  color: var(--primary-color);
  font-weight: 500;
}

.btn-check:focus + .btn,
.btn:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

.input-group-text {
  background: var(--black-eigengrau);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.input-group-text.active {
  background: var(--primary-color);
  color: var(--black-eigengrau);
}

.card-plain .buttons {
  margin-top: 2rem;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-eigengrau);
}

.btn-primary:hover {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

.btn-primary:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}
</style>
